import { useCallback } from "react";
import { useLanguage } from "../../contexts/LanguageContext";
import {
  type SupportedLanguage,
  formatTranslation,
} from "../../utils/translations";

export type GeminiTranslationKey =
  | "gemini.title"
  | "gemini.badge"
  | "gemini.description"
  | "gemini.server_url"
  | "gemini.server_url_hint"
  | "gemini.generate_token"
  | "gemini.generating_token"
  | "gemini.token_label"
  | "gemini.token_warning"
  | "gemini.guide_title"
  | "gemini.step_1"
  | "gemini.step_2"
  | "gemini.step_3"
  | "gemini.step_4"
  | "gemini.scheduled_title"
  | "gemini.scheduled_desc"
  | "gemini.scheduled_prompt"
  | "gemini.copy"
  | "gemini.copied"
  | "gemini.error";

const en: Record<GeminiTranslationKey, string> = {
  "gemini.title": "Google Gemini Connected App (MCP)",
  "gemini.badge": "Custom MCP Server",
  "gemini.description":
    "Connect your PriceLoss account to Google Gemini via Remote MCP Server to ask about price drops, check watch history, and receive automated scheduled alerts.",
  "gemini.server_url": "MCP Server URL",
  "gemini.server_url_hint":
    "Use this streamable HTTP URL in Gemini > Connected Apps > + Custom > Connect to an MCP server.",
  "gemini.generate_token": "Generate Gemini Token",
  "gemini.generating_token": "Generating...",
  "gemini.token_label": "Your Personal Integration Token",
  "gemini.token_warning":
    "Copy this token now. For your security, it will never be displayed again.",
  "gemini.guide_title": "Connection Steps (Gemini Interface)",
  "gemini.step_1":
    'Open Gemini, navigate to Connected Apps, click "+ Custom" and select "Connect to an MCP server".',
  "gemini.step_2": "Paste the MCP Server URL into the input field.",
  "gemini.step_3":
    'Click "Additional settings (Show more)", enter Client ID: priceloss-gemini and Client secret: priceloss-gemini-secret.',
  "gemini.step_4":
    "Click Next to confirm and authorize PriceLoss. Tools will then be active in Gemini conversations.",
  "gemini.scheduled_title":
    "Daily Price-Drop Automation (Gemini Scheduled Prompts)",
  "gemini.scheduled_desc":
    'In Gemini’s sidebar under "Scheduled", create a recurring prompt to receive automatic daily deal digests:',
  "gemini.scheduled_prompt":
    "Check PriceLoss for any unread price drops on my tracked products. If there are drops, summarize each item with previous price, current price, and link, then acknowledge them.",
  "gemini.copy": "Copy",
  "gemini.copied": "Copied!",
  "gemini.error": "Failed to generate token. Please try again.",
};

const ar: Record<GeminiTranslationKey, string> = {
  "gemini.title": "تطبيق Google Gemini المتصل (MCP)",
  "gemini.badge": "خادم MCP مخصص",
  "gemini.description":
    "اربط حساب PriceLoss بـ Google Gemini عبر خادم MCP لتتبع انخفاض الأسعار، وفحص سجل المراقبات، وتلقي تنبيهات مجدولة تلقائية.",
  "gemini.server_url": "رابط خادم MCP",
  "gemini.server_url_hint":
    "استخدم هذا الرابط في Gemini > التطبيقات المتصلة > + مخصص > الاتصال بخادم MCP.",
  "gemini.generate_token": "إنشاء رمز الربط لـ Gemini",
  "gemini.generating_token": "جارٍ الإنشاء...",
  "gemini.token_label": "رمز التكامل الشخصي الخاص بك",
  "gemini.token_warning":
    "انسخ هذا الرمز الآن. لأسباب أمنية، لن يتم عرضه مرة أخرى.",
  "gemini.guide_title": "خطوات الربط (واجهة Gemini)",
  "gemini.step_1":
    'افتح Gemini، وانتقل إلى التطبيقات المتصلة (Connected Apps)، ثم اضغط "+ مخصص" واختر "الاتصال بخادم MCP".',
  "gemini.step_2": "الصق رابط خادم MCP في حقل الرابط.",
  "gemini.step_3":
    'اضغط على "Additional settings (Show more)"، وأدخل معرف العميل: priceloss-gemini والسر: priceloss-gemini-secret.',
  "gemini.step_4":
    "اضغط Next للموافقة والتفويض. ستصبح أدوات PriceLoss متاحة للاستخدام في محادثاتك مع Gemini فوراً.",
  "gemini.scheduled_title":
    "أتمتة تنبيهات انخفاض الأسعار اليومية (مطالبات Gemini المجدولة)",
  "gemini.scheduled_desc":
    'في القائمة الجانبية لـ Gemini تحت "Scheduled" (المجدولة)، أنشئ مطالبة متكررة لتلقي ملخصات يومية تلقائية:',
  "gemini.scheduled_prompt":
    "تحقق من PriceLoss لمعرفة أي انخفاضات جديدة في أسعار المنتجات المراقبة. إذا وُجدت انخفاضات، اذكر اسم المنتج والسعر السابق والسعر الحالي والرابط، ثم أكد استلام التنبيهات.",
  "gemini.copy": "نسخ",
  "gemini.copied": "تم النسخ!",
  "gemini.error": "فشل إنشاء الرمز. يرجى المحاولة مرة أخرى.",
};

const fr: Record<GeminiTranslationKey, string> = {
  "gemini.title": "Application connectée Google Gemini (MCP)",
  "gemini.badge": "Serveur MCP personnalisé",
  "gemini.description":
    "Connectez votre compte PriceLoss à Google Gemini via un serveur MCP distant pour suivre les baisses de prix et recevoir des alertes planifiées.",
  "gemini.server_url": "URL du serveur MCP",
  "gemini.server_url_hint":
    "Utilisez cette URL dans Gemini > Applications connectées > + Personnalisé > Se connecter à un serveur MCP.",
  "gemini.generate_token": "Générer un jeton Gemini",
  "gemini.generating_token": "Génération...",
  "gemini.token_label": "Votre jeton d’intégration personnel",
  "gemini.token_warning":
    "Copiez ce jeton maintenant. Pour votre sécurité, il ne sera plus jamais affiché.",
  "gemini.guide_title": "Étapes de connexion (Interface Gemini)",
  "gemini.step_1":
    'Ouvrez Gemini, accédez aux Applications connectées, cliquez sur "+ Personnalisé" et choisissez "Se connecter à un serveur MCP".',
  "gemini.step_2": "Collez l’URL du serveur MCP dans le champ correspondant.",
  "gemini.step_3":
    'Cliquez sur "Additional settings", saisissez Client ID: priceloss-gemini et Client secret: priceloss-gemini-secret.',
  "gemini.step_4":
    "Cliquez sur Next pour autoriser l'accès. Les outils PriceLoss seront actifs dans Gemini.",
  "gemini.scheduled_title":
    "Alertes quotidiennes automatiques (Prompts planifiés Gemini)",
  "gemini.scheduled_desc":
    'Dans le menu latéral de Gemini sous "Scheduled", créez un prompt récurrent pour recevoir un résumé quotidien :',
  "gemini.scheduled_prompt":
    "Vérifiez sur PriceLoss s’il y a des baisses de prix non lues sur mes produits suivis. Résumez chaque offre puis marquez-les comme lues.",
  "gemini.copy": "Copier",
  "gemini.copied": "Copié !",
  "gemini.error": "Échec de la génération du jeton. Veuillez réessayer.",
};

const pt: Record<GeminiTranslationKey, string> = {
  "gemini.title": "Aplicativo Conectado Google Gemini (MCP)",
  "gemini.badge": "Servidor MCP personalizado",
  "gemini.description":
    "Conecte sua conta PriceLoss ao Google Gemini via servidor MCP remoto para consultar quedas de preço e receber alertas programados.",
  "gemini.server_url": "URL do Servidor MCP",
  "gemini.server_url_hint":
    "Use esta URL no Gemini > Aplicativos Conectados > + Personalizado > Conectar a um servidor MCP.",
  "gemini.generate_token": "Gerar Token Gemini",
  "gemini.generating_token": "Gerando...",
  "gemini.token_label": "Seu token de integração pessoal",
  "gemini.token_warning":
    "Copie este token agora. Por segurança, ele não será exibido novamente.",
  "gemini.guide_title": "Passos para conexão (Interface Gemini)",
  "gemini.step_1":
    'Abra o Gemini, acesse Aplicativos Conectados, clique em "+ Personalizado" e selecione "Conectar a um servidor MCP".',
  "gemini.step_2": "Cole a URL do Servidor MCP no campo correspondente.",
  "gemini.step_3":
    'Clique em "Additional settings", insira Client ID: priceloss-gemini e Client secret: priceloss-gemini-secret.',
  "gemini.step_4":
    "Clique em Next para autorizar o PriceLoss e ativar as ferramentas no Gemini.",
  "gemini.scheduled_title":
    "Automação Diária de Quedas de Preço (Prompts Agendados do Gemini)",
  "gemini.scheduled_desc":
    'No menu lateral do Gemini em "Scheduled", crie um prompt recorrente para receber resumos diários automáticos:',
  "gemini.scheduled_prompt":
    "Verifique no PriceLoss se há quedas de preço não lidas nos meus produtos. Resuma cada item e confirme os alertas.",
  "gemini.copy": "Copiar",
  "gemini.copied": "Copiado!",
  "gemini.error": "Falha ao gerar o token. Tente novamente.",
};

const es: Record<GeminiTranslationKey, string> = {
  "gemini.title": "Aplicación Conectada Google Gemini (MCP)",
  "gemini.badge": "Servidor MCP personalizado",
  "gemini.description":
    "Conecte su cuenta PriceLoss a Google Gemini mediante un servidor MCP remoto para consultar caídas de precio y recibir alertas programadas.",
  "gemini.server_url": "URL del servidor MCP",
  "gemini.server_url_hint":
    "Use esta URL en Gemini > Aplicaciones conectadas > + Personalizado > Conectar a un servidor MCP.",
  "gemini.generate_token": "Generar token Gemini",
  "gemini.generating_token": "Generando...",
  "gemini.token_label": "Su token de integración personal",
  "gemini.token_warning":
    "Copie este token ahora. Por seguridad, no se volverá a mostrar.",
  "gemini.guide_title": "Pasos de conexión (Interfaz de Gemini)",
  "gemini.step_1":
    'Abra Gemini, vaya a Aplicaciones conectadas, haga clic en "+ Personalizado" y seleccione "Conectar a um servidor MCP".',
  "gemini.step_2": "Pegue la URL del servidor MCP en el campo correspondiente.",
  "gemini.step_3":
    'Haga clic en "Additional settings", ingrese Client ID: priceloss-gemini y Client secret: priceloss-gemini-secret.',
  "gemini.step_4":
    "Haga clic en Next para autorizar PriceLoss y activar las herramientas en Gemini.",
  "gemini.scheduled_title":
    "Automatización diaria de caídas de precio (Prompts programados de Gemini)",
  "gemini.scheduled_desc":
    'En el menú lateral de Gemini bajo "Scheduled", cree un prompt recurrente para recibir resúmenes automáticos:',
  "gemini.scheduled_prompt":
    "Consulte en PriceLoss si hay caídas de precio no leídas en mis productos. Resuma cada oferta y confirme las alertas.",
  "gemini.copy": "Copiar",
  "gemini.copied": "¡Copiado!",
  "gemini.error": "Error al generar el token. Inténtelo de nuevo.",
};

const translations: Record<
  SupportedLanguage,
  Record<GeminiTranslationKey, string>
> = {
  en,
  ar,
  fr,
  pt,
  es,
};

export function useGeminiTranslation(): (
  key: GeminiTranslationKey,
  params?: Record<string, string | number>,
) => string {
  const { language } = useLanguage();
  return useCallback(
    (
      key: GeminiTranslationKey,
      params?: Record<string, string | number>,
    ): string => {
      const dict = translations[language] ?? translations.en;
      const raw = dict[key] ?? translations.en[key] ?? key;
      return formatTranslation(raw, params);
    },
    [language],
  );
}
